import mustache from "mustache";
import { IConnectRender } from "../../definitions/IConnectRender";
import template from "./secondSidebarItemTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let SECOND_SIDEBAR_ITEM_ID = 0;

/** Item base del segundo sidebar (Render/Connect). Diseño único para todos los
 *  módulos: fila clickeable con label (p. ej. la fecha) y un contenedor que se
 *  expande al seleccionarse — el contenido expandido es lo único que cambia por
 *  módulo (`getExpandedHtml`). El acordeón (uno expandido a la vez) lo coordina
 *  `BaseSecondSidebarRender` vía `setOnClick`/`select`/`deselect`. */
export abstract class BaseSecondSidebarItemRender implements IConnectRender {
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _itemId: number;

  constructor() {
    SECOND_SIDEBAR_ITEM_ID += 1;
    this._itemId = SECOND_SIDEBAR_ITEM_ID;
  }

  // ─── Hooks de la subclase ───

  /** Label de la fila/selector (p. ej. "Abril 12") — estilo base. */
  protected abstract getLabel(): string;

  /** Título del contenido expandido (p. ej. "CAÑA / BROCA") — estilo base. */
  protected abstract getTitle(): string;

  /** HTML adicional bajo el título — lo construye cada módulo. */
  protected abstract getItemContent(): string;

  /** Listeners del contenido expandido (se llama tras inyectarlo). */
  protected connectExpanded(): void {}

  /** Limpieza del contenido expandido (se llama al colapsar/disponer). */
  protected disposeExpanded(): void {}

  /** Efecto al seleccionarse (p. ej. pintar el vuelo en el mapa). */
  protected onSelected(): void {}

  // ─── IConnectRender ───

  /** Fila con label + contenedor expandido (oculto). */
  render(): string {
    return mustache.render(template, {
      id: this._itemId,
      label: this.getLabel(),
    });
  }

  /** Captura los elementos y cablea el click de la fila. */
  connect(): void {
    this._labelEl = document.getElementById(`ss-item-label-${this._itemId}`);
    this._contentEl = document.getElementById(
      `ss-item-content-${this._itemId}`,
    );
    this._labelEl?.addEventListener("click", this._onLabelClick);
  }

  /** Libera el listener y el contenido expandido si estaba abierto. */
  dispose(): void {
    this._labelEl?.removeEventListener("click", this._onLabelClick);
    if (this._selected) this.disposeExpanded();
    this._selected = false;
  }

  // ─── Acordeón (lo maneja el sidebar base) ───

  /** Registra el handler de click de la fila (lo usa el sidebar para el
   *  acordeón). */
  setOnClick(handler: () => void): void {
    this._onClick = handler;
  }

  /** Expande el item: marca la fila activa e inyecta título (estilo base, si
   *  `getTitle()` no viene vacío) + contenido del módulo, y los conecta. */
  select(): void {
    if (this._selected) return;
    this._selected = true;
    this._labelEl?.classList.add("active");
    if (this._contentEl) {
      const title = this.getTitle();
      this._contentEl.innerHTML =
        (title ? `<div class="ss-item-title">${title}</div>` : "") +
        this.getItemContent();
      this._contentEl.style.display = "";
    }
    this.connectExpanded();
    this.onSelected();
  }

  /** Colapsa el item: dispone el contenido y desmarca la fila. */
  deselect(): void {
    if (!this._selected) return;
    this._selected = false;
    this.disposeExpanded();
    if (this._contentEl) {
      this._contentEl.innerHTML = "";
      this._contentEl.style.display = "none";
    }
    this._labelEl?.classList.remove("active");
  }

  /** Fila del label (disponible tras `connect`). */
  private _labelEl: HTMLElement | null = null;
  /** Contenedor del contenido expandido (disponible tras `connect`). */
  private _contentEl: HTMLElement | null = null;
  /** Si el item está expandido. */
  private _selected = false;
  /** Handler de click registrado por el sidebar (acordeón). */
  private _onClick: (() => void) | null = null;

  private _onLabelClick = (): void => this._onClick?.();
}
